import type { RouteLocationRaw } from 'vue-router'

export interface ListElement {
  key: string
  title: LangString
  description: LangString
  image: ImageSelection
  data?: any
  type?: Key
  link?: string
  internalLink?: RouteLocationRaw | string
  contributor?: string
  source?: LangString
  rights?: LangString
  rightsType?: string
  ownerOrgId?: string
  def?: CurationPartDefinition
  searchStatus?: string
  latlon?: LatLon
  readNext?: () => void
  id?: string
}
export const useListElement = () => {
  const dataStore = useDataStore()
  const router = useRouter()
  const config = useRuntimeConfig()
  const { compImageSelection } = useImageSelection()

  function toListElement(def: CurationPartDefinition): ListElement {
    switch (def.type) {
      case 'jps-curation-list-curation':
        const c = dataStore.curation_meta(def.id)
        if (c)
          return {
            def,
            type: 'curation',
            key: def.cpid,
            data: c,
            title: c.title,
            latlon: def.latlon,
            description: !isEmpty(def.description)
              ? def.description
              : c.summary,
            image: compImageSelection(def.image || c.image),
            internalLink: `/gallery/${c.id}`,
            ownerOrgId: c.permission.ownerOrg,
            id: c.id,
          }
        break
      case 'jps-curation-list-csearch':
        const cs = dataStore.csearch(def.id)
        if (cs)
          return {
            def,
            type: 'csearch',
            key: def.cpid,
            data: cs,
            title: cs.name,
            image: compImageSelection(def.image || cs.image),
            description: !isEmpty(def.description)
              ? def.description
              : cs.description,
            ownerOrgId: cs.permission.ownerOrg,
            internalLink:
              cs.type === 'NORMAL'
                ? `/csearch/${cs.id}`
                : null,
            id: cs.id,
          }
        break
      case 'jps-curation-list-database':
        const db = dataStore.database(def.id)
        if (db)
          return {
            def,
            type: 'database',
            key: def.cpid,
            data: db,
            title: db.name,
            image: compImageSelection(def.image || db.image),
            source: db.name,
            description: !isEmpty(def.description)
              ? def.description
              : db.description,
            link: db.url,
            internalLink: `/database/${db.id}`,
            ownerOrgId: db.permission.ownerOrg,
            id: db.id,
          }
        break
      case 'jps-curation-list-external-link':
        return {
          def,
          link: def.url,
          internalLink: (def.url?.startsWith("/")) ? def.url : null,
          key: def.cpid,
          latlon: def.latlon,
          image: compImageSelection(def.image),
          description: def.description,
          title: def.title,
        }
      case 'jps-curation-list-item':
        const i = dataStore.item(def.id)
        if (i) {
          const e: ListElement = {
            def,
            data: i,
            type: 'item',
            key: def.cpid,
            rightsType: i.common.contentsRightsType,
            ownerOrgId: i.common.ownerOrg,
            link: def.linkUrl || i.common.linkUrl,
            image: compImageSelection(
              def.image || {
                imgType: 'ITEM',
                itemId: i.id,
                url: isEmptyArray(i.common.thumbnailUrl)
                  ? null
                  : i.common.thumbnailUrl[0],
              },
            ),
            contributor: i.common.contributor
              ? i.common.contributor.join(',')
              : '',
            description: !isEmpty(def.description)
              ? def.description
              : {
                  ja: escapeTag(i.common.description),
                  en: escapeTag(i.common.descriptionEn),
                },
            title: !isEmpty(def.title)
              ? def.title
              : { ja: i.common.title, en: i.common.titleEn },
            internalLink: `/item/${i.id}${def.image && def.image.infoJsonUrl ? '?info=' + def.image.infoJsonUrl : ''}`,
            latlon: def.latlon || i.common.coordinates,
            id: i.id,
          }
          if (isEmpty(e.image.title)) {
            e.image.title = e.title
          }
          return e
        }
        break
      case 'jps-curation-list-organization':
        const org = dataStore.organization(def.id)
        if (org) {
          return {
            def,
            type: 'organization',
            data: org,
            key: def.cpid,
            latlon: def.latlon,
            source: org.name,
            link: org.url,
            image: compImageSelection(def.image || org.image),
            description: !isEmpty(def.description)
              ? def.description
              : org.description,
            title: org.name,
            internalLink:
              org.type === 'NORMAL'
                ? `/organization/${org.id}`
                : `/project/${org.id}`,
            id: org.id,
          }
        }
        break
      case 'jps-curation-list-search-result':
        return {
          def,
          key: def.cpid,
          image: null,
          readNext: () => {}, //分岐のためのHack
          title: { ja: '検索結果' },
          description: { ja: '' },
        }
    }
    return {
      def,
      key: def.cpid,
      title: { ja: '' },
      image: null,
      description: { ja: '' },
    }
  }

  function internalLinkToUrl(link: RouteLocationRaw) {
    if (!link) return ''
    return config.public.serviceBaseUrl.slice(0, -1) + router.resolve(link).href
  }

  function dataToListElement(type: Key, data: any): ListElement {
    if (data) {
      switch (type) {
        case 'curation':
          const curation = data as Curation
          return {
            type,
            data,
            key: curation.id,
            title: curation.title,
            description: curation.summary,
            image: curation.image,
            internalLink: `/gallery/${curation.id}`,
            ownerOrgId: curation.permission?.ownerOrg,
          }
        case 'csearch':
          const csearch = data as CustomSearchDefinition
          return {
            type,
            data,
            key: csearch.id,
            title: csearch.name,
            image: csearch.image,
            description: csearch.description,
            ownerOrgId: csearch.permission?.ownerOrg,
            internalLink:
              csearch.type === 'NORMAL'
                ? `/csearch/${csearch.id}?csid=${csearch.id}`
                : null,
          }
        case 'database':
          const database = data as Database
          return {
            type,
            data,
            key: database.id,
            title: database.name,
            image: database.image,
            source: database.name,
            description: database.description,
            link: database.url,
            internalLink: `/database/${database.id}`,
            ownerOrgId: database.permission?.ownerOrg,
          }
        case 'item':
          const item = data as Item
          return {
            type,
            data,
            key: item.id,
            rightsType: item.common?.contentsRightsType,
            ownerOrgId: item.common?.ownerOrg,
            link: item.common?.linkUrl,
            image: {
              imgType: 'ITEM',
              itemId: item.id,
              url: !isEmptyArray(item.common?.thumbnailUrl)
                ? item.common.thumbnailUrl[0]
                : '',
            },
            contributor: item.common?.contributor
              ? item.common?.contributor.join(',')
              : '',
            description: {
              ja: escapeTag(item.common?.description),
              en: escapeTag(item.common?.descriptionEn),
            },
            title: {
              ja: item.common?.title,
              en: item.common?.titleEn,
            },
            latlon: item.common?.coordinates,
            internalLink: `/item/${item.id}`,
          }
        case 'organization':
          const organization = data as Organization
          return {
            type,
            data,
            key: organization.id,
            source: organization.name,
            link: organization.url,
            image: organization.image,
            description: organization.description,
            title: organization.name,
            internalLink: `/organization/${organization.id}`,
          }
        case 'project':
          const project = data as Organization
          return {
            type,
            data,
            key: project.id,
            source: project.name,
            link: project.url,
            image: project.image,
            description: project.description,
            title: project.name,
            internalLink: `/project/${project.id}`,
          }
      }
    }
    return {
      key: '',
      title: { ja: '' },
      image: null,
      description: { ja: '' },
    }
  }

  function toModalInfo(e: ListElement): any {
    return Object.freeze({
      image: e.image,
      description: e.description,
      title: e.title,
      link: e.link,
      source: e.image ? e.image.source || e.source : e.source,
      sourceUrl: e.image ? e.image.sourceUrl : null,
      rights: e.image ? e.image.contentsRights || e.rights : e.rights,
      rightsType: e.image
        ? e.image.contentsRightsType || e.rightsType
        : e.rightsType,
      type: e.type,
      data: e.data,
      ownerOrgId: e.ownerOrgId,
      internalLink: e.internalLink,
      id: e.id
    })
  }
  return { toListElement, internalLinkToUrl, dataToListElement, toModalInfo }
}
